// oklch.ts — sRGB ↔ OKLab ↔ OKLCH conversions plus WCAG contrast.
// OKLCH is perceptually uniform, so it's the right space for gradient descent
// over palette colors: small parameter changes produce small visual changes.

export interface OKLCH { L: number; C: number; h: number }
export interface RGB { r: number; g: number; b: number }

export const srgbToLin = (c: number) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
export const linToSrgb = (c: number) =>
    c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

export function rgbToOklab(rgb: RGB): { L: number; a: number; b: number } {
    const r = srgbToLin(rgb.r), g = srgbToLin(rgb.g), b = srgbToLin(rgb.b);
    const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
    const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
    const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
    const l_ = Math.cbrt(l), m_ = Math.cbrt(m), s_ = Math.cbrt(s);
    return {
        L: 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
        a: 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
        b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
    };
}

export function oklabToLinearRgb(L: number, a: number, b: number) {
    const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = L - 0.0894841775 * a - 1.2914855480 * b;
    const l = l_ * l_ * l_, m = m_ * m_ * m_, s = s_ * s_ * s_;
    return {
        r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
        g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
        b: -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
    };
}

export function oklchToRgb(c: OKLCH): RGB {
    const rad = (c.h * Math.PI) / 180;
    const a = c.C * Math.cos(rad);
    const b = c.C * Math.sin(rad);
    const lin = oklabToLinearRgb(c.L, a, b);
    return {
        r: clamp01(linToSrgb(lin.r)),
        g: clamp01(linToSrgb(lin.g)),
        b: clamp01(linToSrgb(lin.b)),
    };
}

// Out-of-gamut measure (linear sRGB). Returns sum of squared excursions
// past [0,1] on each channel — zero means in-gamut, larger means clipping.
export function gamutExcess(c: OKLCH): number {
    const rad = (c.h * Math.PI) / 180;
    const a = c.C * Math.cos(rad);
    const b = c.C * Math.sin(rad);
    const lin = oklabToLinearRgb(c.L, a, b);
    const over = (v: number) => Math.max(0, v - 1) + Math.max(0, -v);
    const dr = over(lin.r), dg = over(lin.g), db = over(lin.b);
    return dr * dr + dg * dg + db * db;
}

export function rgbToOklch(rgb: RGB): OKLCH {
    const lab = rgbToOklab(rgb);
    const C = Math.hypot(lab.a, lab.b);
    let h = (Math.atan2(lab.b, lab.a) * 180) / Math.PI;
    if (h < 0) h += 360;
    return { L: lab.L, C, h };
}

export function oklchToHex(c: OKLCH): string {
    const rgb = oklchToRgb(c);
    const to = (v: number) => Math.round(v * 255).toString(16).padStart(2, '0');
    return `#${to(rgb.r)}${to(rgb.g)}${to(rgb.b)}`;
}

export function rgbToHex(rgb: RGB): string {
    const to = (v: number) => Math.round(v * 255).toString(16).padStart(2, '0');
    return `#${to(rgb.r)}${to(rgb.g)}${to(rgb.b)}`;
}

export function hexToOklch(hex: string): OKLCH | null {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return null;
    return rgbToOklch({
        r: parseInt(m[1].slice(0, 2), 16) / 255,
        g: parseInt(m[1].slice(2, 4), 16) / 255,
        b: parseInt(m[1].slice(4, 6), 16) / 255,
    });
}

export function relLuminance(rgb: RGB): number {
    const lin = (c: number) => srgbToLin(c);
    return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
}

export function contrastRatio(a: RGB, b: RGB): number {
    const La = relLuminance(a);
    const Lb = relLuminance(b);
    const hi = La > Lb ? La : Lb;
    const lo = La > Lb ? Lb : La;
    return (hi + 0.05) / (lo + 0.05);
}

// Pick an OKLCH color at the given hue that satisfies contrast against both
// the page background and a card/surface background, while staying inside a
// chroma corridor and in sRGB gamut. Used to derive per-name accent colors
// (e.g., role/scope chip outlines) that are guaranteed-readable in both
// light and dark themes. Cheap grid search — ~80 evaluations per call.
export function solveContrastColor(
    hue: number,
    bgRgb: RGB,
    surfaceRgb: RGB,
    opts?: { minBgContrast?: number; minSurfaceContrast?: number; cMin?: number; cMax?: number },
): OKLCH {
    const minBg = opts?.minBgContrast ?? 4.0;
    const minSurf = opts?.minSurfaceContrast ?? 3.0;
    const cMin = opts?.cMin ?? 0.10;
    const cMax = opts?.cMax ?? 0.18;
    let best: OKLCH = { L: 0.55, C: (cMin + cMax) / 2, h: hue };
    let bestLoss = Infinity;
    for (let L = 0.20; L <= 0.95; L += 0.05) {
        for (let C = cMin; C <= cMax + 1e-9; C += 0.02) {
            const candidate: OKLCH = { L, C, h: hue };
            const ge = gamutExcess(candidate);
            if (ge > 1e-3) continue;
            const rgb = oklchToRgb(candidate);
            const cBg = contrastRatio(rgb, bgRgb);
            const cSurf = contrastRatio(rgb, surfaceRgb);
            const dBg = Math.max(0, minBg - cBg);
            const dSurf = Math.max(0, minSurf - cSurf);
            const loss = 3 * dBg * dBg + 1.5 * dSurf * dSurf;
            if (loss < bestLoss) { bestLoss = loss; best = candidate; }
        }
    }
    return best;
}
